import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { AgentRunStep } from '../../types/agent';
import { fmtDuration, fmtTokens } from './agentUi';

const KIND_META: Record<string, { icon: string; label: string; tone: string }> = {
  context: { icon: '🧭', label: 'Context', tone: 'border-gray-300 dark:border-gray-600' },
  model_turn: { icon: '💭', label: 'Arie', tone: 'border-blue-300 dark:border-blue-700' },
  tool_call: { icon: '🔧', label: 'Tool', tone: 'border-gray-300 dark:border-gray-600' },
  tool_result: { icon: '📥', label: 'Result', tone: 'border-gray-300 dark:border-gray-600' },
  decision: { icon: '👻', label: 'Shadow', tone: 'border-purple-300 dark:border-purple-700' },
  approval_requested: { icon: '✋', label: 'Approval requested', tone: 'border-amber-400 dark:border-amber-600' },
  approval_resolved: { icon: '✅', label: 'Approval', tone: 'border-green-400 dark:border-green-700' },
  artifact: { icon: '📦', label: 'Artifact', tone: 'border-teal-300 dark:border-teal-700' },
  notification: { icon: '🔔', label: 'Notice', tone: 'border-gray-300 dark:border-gray-600' },
  error: { icon: '⛔', label: 'Error', tone: 'border-red-400 dark:border-red-700' },
  policy_block: { icon: '🚫', label: 'Blocked by policy', tone: 'border-red-300 dark:border-red-700' },
};

const READ_TOOLS = new Set([
  'search_customers', 'get_customer_details', 'search_projects', 'list_documents', 'get_workflow_catalog', 'get_workflow_suggestions',
  'get_project_status', 'search_reports', 'get_report', 'get_document_extraction', 'read_document', 'search_agency_knowledge',
  'get_run_status', 'list_tasks', 'route_documents', 'get_attention_items', 'get_inbound_email', 'list_acord_forms', 'list_certificate_holders',
]);

const JsonBlock: React.FC<{ label: string; value: unknown }> = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const text = JSON.stringify(value, null, 2);
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</span>
        <button
          className="text-[11px] text-primary hover:underline"
          onClick={() => {
            navigator.clipboard?.writeText(text).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="mt-1 max-h-72 overflow-auto text-xs bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded p-2 whitespace-pre-wrap break-words">{text}</pre>
    </div>
  );
};

const StepRow: React.FC<{ step: AgentRunStep; showReads: boolean }> = ({ step, showReads }) => {
  const [open, setOpen] = useState(false);
  const isRead = step.kind === 'tool_call' && step.tool && READ_TOOLS.has(step.tool);
  if (isRead && !showReads) return null;
  const meta = KIND_META[step.kind] || { icon: '•', label: step.kind, tone: 'border-gray-300' };
  const failed = step.status === 'failed';
  const hasDetail = !!(step.args || step.result);
  const title =
    step.kind === 'tool_call'
      ? isRead
        ? `Looked up: ${step.tool}`
        : step.modelText || step.tool
      : step.kind === 'model_turn'
      ? null
      : step.modelText || meta.label;

  return (
    <li className={`relative pl-8 pb-4 border-l-2 ${meta.tone} ml-3`}>
      <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-xs">{meta.icon}</span>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium">{meta.label}</span>
            {step.tool && step.kind !== 'tool_call' && <code className="text-[11px]">{step.tool}</code>}
            {failed && <span className="text-red-600 dark:text-red-400 font-medium">failed</span>}
            {step.status === 'blocked' && <span className="text-red-600 dark:text-red-400 font-medium">blocked</span>}
            {step.status === 'pending' && <span className="text-amber-600 dark:text-amber-400 font-medium">pending</span>}
            {step.durationMs ? <span>· {fmtDuration(step.durationMs)}</span> : null}
            {step.tokens && (step.tokens.in || step.tokens.out) ? <span>· {fmtTokens(step.tokens.in)}→{fmtTokens(step.tokens.out)} tok</span> : null}
          </div>
          {title && <div className={`text-sm mt-0.5 ${failed ? 'text-red-700 dark:text-red-300' : 'text-gray-800 dark:text-gray-100'}`}>{title}</div>}
          {step.kind === 'model_turn' && step.modelText && (
            <div className="prose prose-sm dark:prose-invert max-w-none mt-1 text-gray-800 dark:text-gray-100">
              <ReactMarkdown>{step.modelText}</ReactMarkdown>
            </div>
          )}
          {failed && step.result?.error && <div className="text-xs text-red-600 dark:text-red-400 mt-0.5">{String(step.result.error)}</div>}
        </div>
        {hasDetail && (
          <button className="text-xs text-primary hover:underline flex-shrink-0" onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : 'Details'}
          </button>
        )}
      </div>
      {open && (
        <div>
          {step.args && <JsonBlock label="Arguments" value={step.args} />}
          {step.result && <JsonBlock label="Result" value={step.result} />}
          {step.resultS3Key && <p className="text-[11px] text-gray-500 mt-1">Full result archived ({step.resultS3Key.split('/').pop()}).</p>}
        </div>
      )}
    </li>
  );
};

const RunTimeline: React.FC<{ steps: AgentRunStep[]; live?: boolean }> = ({ steps, live }) => {
  const [showReads, setShowReads] = useState(false);
  const readCount = steps.filter((s) => s.kind === 'tool_call' && s.tool && READ_TOOLS.has(s.tool)).length;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Trace</h3>
        {readCount > 0 && (
          <label className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showReads} onChange={(e) => setShowReads(e.target.checked)} />
            Show {readCount} lookup{readCount === 1 ? '' : 's'}
          </label>
        )}
      </div>
      {steps.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{live ? 'Waiting for the run to start…' : 'No steps recorded.'}</p>
      ) : (
        <ol className="mt-2">
          {steps.map((s) => (
            <StepRow key={s.id} step={s} showReads={showReads} />
          ))}
          {live && (
            <li className="relative pl-8 ml-3 border-l-2 border-dashed border-blue-300">
              <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Working…</span>
            </li>
          )}
        </ol>
      )}
    </div>
  );
};

export default RunTimeline;
